import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLanguage, pick } from "@/context/LanguageContext";
import { PageHero, StatusBadge, Spinner } from "@/components/common/Bits";
import { ArrowRight, MapPin, Calendar, Users } from "lucide-react";

const CATEGORY_KEYS = ["", "industry", "education", "social", "culture", "research"];
const STATUS_KEYS = ["", "planned", "ongoing", "completed"];

export function ProjectsListPage() {
  const { t, lang } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.append("category", category);
    if (status) params.append("status", status);
    api.get(`/projects?${params}`).then(r => setProjects(r.data)).finally(() => setLoading(false));
  }, [category, status]);

  const projectCategories = CATEGORY_KEYS.map(k => ({ key: k, label: (t.project_categories && t.project_categories[k]) || k || t.filter_all }));
  const projectStatuses = STATUS_KEYS.map(k => ({ key: k, label: (t.project_status_labels && t.project_status_labels[k]) || k || t.filter_all_status }));

  return (
    <div data-testid="page-projects">
      <PageHero overline={t.ov_projects} title={t.projects_hero_title} description={t.projects_hero_desc} image="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"/>
      <section className="py-16 useko-container">
        {/* Filters */}
        <div className="mb-10 flex flex-col md:flex-row gap-4 md:items-center">
          <div className="flex flex-wrap gap-2" data-testid="filter-categories">
            {projectCategories.map(c => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${category === c.key ? "bg-[#1F8A5B] text-white border-[#1F8A5B]" : "bg-white text-[#102A43] border-[#E5E7EB] hover:border-[#1F8A5B]"}`}
                data-testid={`filter-cat-${c.key || "all"}`}
              >{c.label}</button>
            ))}
          </div>
          <div className="md:ml-auto">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-4 py-2 rounded-lg border border-[#E5E7EB] text-sm bg-white text-[#102A43]" data-testid="filter-status">
              {projectStatuses.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16"><Spinner/></div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16 border border-dashed rounded-2xl border-[#E5E7EB]"><p className="text-[#4B5563]">{t.no_results}</p></div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map(p => (
              <Link key={p.id} to={`/projeler/${p.slug}`} className="useko-card overflow-hidden group" data-testid={`project-card-${p.slug}`}>
                {p.cover_image && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <StatusBadge status={p.status}/>
                    {p.country && <span className="text-xs text-[#4B5563]">{p.country}</span>}
                  </div>
                  <h3 className="font-heading text-lg font-bold text-[#102A43] line-clamp-2">{pick(p, "title", lang)}</h3>
                  <p className="mt-2 text-sm text-[#4B5563] line-clamp-3">{pick(p, "short_description", lang)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function ProjectDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/projects/${slug}`).then(r => setProject(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="useko-container py-32 text-center">
      <h1 className="font-heading text-3xl font-bold text-[#102A43]">{t.not_found_title}</h1>
      <Link to="/projeler" className="btn-primary mt-6 inline-flex">{t.back_to_projects}</Link>
    </div>
  );

  if (!project) return <div className="py-32 text-center"><Spinner/></div>;

  return (
    <div data-testid={`page-project-${slug}`}>
      {project.cover_image && (
        <div className="relative aspect-[21/9] max-h-[520px] overflow-hidden">
          <img src={project.cover_image} alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"/>
          <div className="absolute inset-0 flex items-end">
            <div className="useko-container pb-10 text-white">
              <div className="flex items-center gap-3 mb-3">
                <StatusBadge status={project.status}/>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-4xl">{pick(project, "title", lang)}</h1>
            </div>
          </div>
        </div>
      )}
      <section className="py-16 useko-container grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="prose-useko">
            <p className="text-lg">{pick(project, "short_description", lang)}</p>
          </div>
          <div className="prose-useko rich-content mt-4" dangerouslySetInnerHTML={{ __html: pick(project, "content", lang) || "" }}/>
        </div>
        <aside className="lg:col-span-4 space-y-4">
          <div className="p-6 useko-card">
            <h3 className="font-heading text-lg font-bold text-[#102A43] mb-4">{t.ov_project}</h3>
            <dl className="space-y-3 text-sm">
              {project.country && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#1F8A5B] flex-shrink-0"/>
                  <div><dt className="text-[#4B5563]">{t.detail_country}</dt><dd className="text-[#102A43] font-medium">{project.country}{project.city ? `, ${project.city}` : ""}</dd></div>
                </div>
              )}
              {project.start_date && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-4 h-4 mt-0.5 text-[#1F8A5B] flex-shrink-0"/>
                  <div><dt className="text-[#4B5563]">{t.detail_start}</dt><dd className="text-[#102A43] font-medium">{project.start_date}</dd></div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Users className="w-4 h-4 mt-0.5 text-[#1F8A5B] flex-shrink-0"/>
                <div><dt className="text-[#4B5563]">{t.detail_category}</dt><dd className="text-[#102A43] font-medium capitalize">{(t.project_categories && t.project_categories[project.category]) || project.category}</dd></div>
              </div>
            </dl>
          </div>
          <Link to="/is-birligi" className="btn-primary w-full justify-center" data-testid="project-cta-partner">{t.btn_learn_more} <ArrowRight className="w-4 h-4"/></Link>
        </aside>
      </section>
    </div>
  );
}

export function SyriaProgramPage() {
  const { t } = useLanguage();
  const cards = [
    { title: t.act_industry, body: t.act_industry_desc },
    { title: t.act_education, body: t.act_education_desc },
    { title: t.act_social, body: t.act_social_desc },
    { title: t.act_culture, body: t.act_culture_desc },
    { title: t.act_research, body: t.act_research_desc },
    { title: t.act_financial, body: t.act_financial_desc },
  ];
  return (
    <div data-testid="page-syria">
      <PageHero overline={t.ov_program} title={t.syria_title} description={t.syria_body} image="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"/>
      <section className="py-20 useko-container">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <div key={i} className="useko-card p-6" data-testid={`syria-card-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-[#EAF7F0] flex items-center justify-center font-heading font-bold text-[#0F5132]">{i + 1}</div>
              <h3 className="mt-4 font-heading text-lg font-bold text-[#102A43]">{c.title}</h3>
              <p className="mt-2 text-sm text-[#4B5563]">{c.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-16 p-10 rounded-3xl bg-[#0F5132] text-white text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">{t.nav_partnership}</h2>
          <p className="mt-3 text-white/85 max-w-2xl mx-auto">{t.partnership_hero_desc}</p>
          <Link to="/is-birligi" className="mt-6 inline-flex items-center gap-2 bg-white text-[#0F5132] font-medium px-6 py-3 rounded-full hover:bg-[#EAF7F0] transition-colors" data-testid="syria-form-cta">
            {t.business_cta_form} <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </section>
    </div>
  );
}
